import React, { useState, useEffect, useRef } from "react";
import BarraFiltros, { SinResultados } from "./BarraFiltros";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, AppBar, Toolbar, Button, Box,
  CircularProgress, IconButton, styled
} from "@mui/material";

import Chip from "@mui/material/Chip";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTag, faEdit, faBan, faCheck } from "@fortawesome/free-solid-svg-icons";
import { useLocation } from "react-router-dom";

import ModalAsignarTags from "../components/ModalAsignarTags";
import ModalProducto from "../components/ModalProducto";
import ModalTagsMultiples from "../components/ModalTagsMultiples";
import ModalDetalleProducto from "../components/ModalDetalleProducto";

import { apiFetch, ApiError } from "../utils/api"; // 

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: "bold",
}));

function ProductosPage() {
  const location = useLocation();

  // 🔎 Normaliza tags escaneados: S'001'002 → S-001-002
  const normalizarTag = (value) => {
    if (!value) return "";
    return String(value)
      .toUpperCase()
      .replace(/'/g, "-")
      .replace(/\s+/g, "")
      .trim();
  };

  const fromFactura = location.state?.fromFactura || false;
  const facturaId = location.state?.factura_id || null;
  const xmlConceptoId = location.state?.xml_concepto_id || null;
  const descripcionFactura = location.state?.descripcion || "";
  const fechaFactura = location.state?.fecha_factura || "";

  const [productos, setProductos] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [personales, setPersonales] = useState([]);
  const [proveedores, setProveedores] = useState([]);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [tagFiltro, setTagFiltro] = useState("");

  const [openProductoModal, setOpenProductoModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [openTagsMultiplesModal, setOpenTagsMultiplesModal] = useState(false);
  const [openTagsModal, setOpenTagsModal] = useState(false);

  const [openDetalleModal, setOpenDetalleModal] = useState(false);
  const [productoDetalle, setProductoDetalle] = useState(null);

  const [selectedProductoId, setSelectedProductoId] = useState(null);

  const [saving, setSaving] = useState(false);
  const searchDebounceRef = useRef(null); // lo dejas por si lo usas después
  const [errors, setErrors] = useState({});

  const [productosParaTags, setProductosParaTags] = useState([]);

  const defaultProducto = {
    descripcion: "",
    modelo: "",
    marca: "",
    numero_serie: "",
    pedido_orden: "",
    fecha_compra: "",
    fecha_mantenimiento: "",
    propio_rentado: "Propio",
    id_almacen: "",
    id_personal: "",
    id_proveedor: "",
    seriesMultiples: [],
    factura_id: null,
    xml_concepto_id: null
  };

  const [newProducto, setNewProducto] = useState({ ...defaultProducto });

  // ===============================
  // CARGA INICIAL
  // ===============================
  useEffect(() => {
    fetchData();

  }, []);

  // ===============================
  // DESDE FACTURA
  // ===============================
  useEffect(() => {
    if (fromFactura) {
      setEditMode(false);
      setNewProducto({
        ...defaultProducto,
        descripcion: descripcionFactura,
        fecha_compra: fechaFactura ? fechaFactura.split("T")[0] : "",
        factura_id: facturaId,
        xml_concepto_id: xmlConceptoId,
      });
      setOpenProductoModal(true);

      Swal.fire(
        "Crear producto",
        "Este producto quedará vinculado a la factura",
        "info"
      );
    }

  }, [fromFactura]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [p, a, pe, pr] = await Promise.all([
        apiFetch("/api/productos"),
        apiFetch("/api/almacenes"),
        apiFetch("/api/personales?is_disabled=false"),
        apiFetch("/api/proveedores")
      ]);

      setProductos(p || []);
      setAlmacenes(a || []);
      setPersonales(pe || []);
      setProveedores(pr || []);
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // DESHABILITAR / HABILITAR
  // ===============================
  const handleToggleDisable = async (producto) => {
    const nuevoEstado = producto.isDisable ? 0 : 1;
    const accion = nuevoEstado ? "deshabilitar" : "habilitar";

    const confirm = await Swal.fire({
      title: `¿${accion.toUpperCase()} producto?`,
      text: producto.descripcion,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí",
      cancelButtonText: "Cancelar"
    });

    if (!confirm.isConfirmed) return;

    await apiFetch(`/api/productos/${producto.id_producto}/status`, {
      method: "PUT",
      body: { isDisable: nuevoEstado }
    });

    Swal.fire("Listo", "Estado actualizado", "success");
    fetchData();
  };

  // ===============================
  // GUARDAR PRODUCTO
  // ===============================
  const handleSaveProducto = async () => {
    setSaving(true);
    try {
      const isEdit = editMode && !!newProducto?.id_producto;

      const formData = new FormData();

      // ✅ evita mandar basura como tag/almacen/responsable/proveedor etc.
      const allowedKeys = [
        "descripcion",
        "modelo",
        "marca",
        "numero_serie",
        "pedido_orden",
        "fecha_compra",
        "fecha_mantenimiento",
        "propio_rentado",
        "id_almacen",
        "id_personal",
        "id_proveedor",
        "factura_id",
        "xml_concepto_id",
      ];

      allowedKeys.forEach((k) => {
        const v = newProducto?.[k];
        if (v === null || v === "" || typeof v === "undefined") return;
        formData.append(k, v);
      });

      // ✅ series múltiples solo en CREAR
      if (!isEdit && Array.isArray(newProducto.seriesMultiples) && newProducto.seriesMultiples.length > 1) {
        formData.append("seriesMultiples", JSON.stringify(newProducto.seriesMultiples));
      }

      const url = isEdit
        ? `/api/productos/${newProducto.id_producto}`
        : `/api/productos`;

      const method = isEdit ? "PUT" : "POST";

      const data = await apiFetch(url, {
        method,
        body: formData,
        showError: false
      });

      setOpenProductoModal(false);
      fetchData();

      // ✅ en edición NO preguntes lo del tag (eso es solo para creado)
      if (isEdit) {
        Swal.fire("Listo", "Producto actualizado", "success");
        return;
      }

      // ---- lo que ya tenías para creado ----
      const productosCreados = data?.creados ? data.creados : [data?.creado].filter(Boolean);

      const ask = await Swal.fire({
        title: "Producto creado",
        text: "¿Deseas asignar el tag ahora?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí",
        cancelButtonText: "Después"
      });

      if (ask.isConfirmed) {
        if (productosCreados.length === 1) {
          setSelectedProductoId(productosCreados[0].id_producto);
          setOpenTagsModal(true);
        } else if (productosCreados.length > 1) {
          setProductosParaTags(productosCreados);
          setOpenTagsMultiplesModal(true);
        }
      }
    } catch (e) {
      if (e instanceof ApiError && e?.body?.code === "CONCEPTO_SIN_DISPONIBLES") {
        const d = e.body;
        Swal.fire({
          icon: "error",
          title: "Sin disponibilidad",
          html: `
          <b>${d.descripcion}</b><br/>
          Disponibles: <b>${d.disponibles}</b><br/>
          Requeridos: <b>${d.requeridos}</b>
        `
        });
      } else {
        Swal.fire("Error", e?.message || "No se pudo guardar el producto.", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  // ===============================
  // BUSQUEDA (incluye tag)
  // ===============================
  const q = (search || "").toLowerCase();
  const qNorm = normalizarTag(search);

  const productosFiltrados = productos.filter(p => {
    const descripcion = (p.descripcion || "").toLowerCase();
    const serie = (p.numero_serie || "").toLowerCase();

    const tagTexto =
      typeof p.tag === "string"
        ? p.tag
        : String(p.tag?.tag || p.tag?.codigo || p.tag?.numero || "");

    const tagLower = tagTexto.toLowerCase();
    const tagNorm = normalizarTag(tagTexto);

    const coincideBusqueda =
      descripcion.includes(q) ||
      serie.includes(q) ||
      tagLower.includes(q) ||
      tagNorm.includes(qNorm);

    const coincideEstado =
      estadoFiltro === "" || (estadoFiltro === "deshabilitados") === Boolean(p.isDisable);

    const coincideTag =
      tagFiltro === "" || (tagFiltro === "con") === Boolean(p.tag);

    return coincideBusqueda && coincideEstado && coincideTag;
  });

  const limpiarFiltros = () => {
    setSearch("");
    setEstadoFiltro("");
    setTagFiltro("");
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Activos
          </Typography>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => {
              setEditMode(false);
              setNewProducto(defaultProducto);
              setOpenProductoModal(true);
            }}
          >
            Nuevo Producto
          </Button>
        </Toolbar>
      </AppBar>

      <BarraFiltros
        busqueda={search}
        onBusqueda={setSearch}
        placeholder="Buscar por descripción, serie o tag"
        filtros={[
          {
            id: "estado",
            tipo: "chips",
            etiqueta: "Estado",
            valor: estadoFiltro,
            onChange: setEstadoFiltro,
            opciones: [
              { valor: "", etiqueta: "Todos" },
              { valor: "activos", etiqueta: "Activos" },
              { valor: "deshabilitados", etiqueta: "Deshabilitados" },
            ],
          },
          {
            id: "tag",
            tipo: "chips",
            etiqueta: "Tag",
            valor: tagFiltro,
            onChange: setTagFiltro,
            opciones: [
              { valor: "", etiqueta: "Todos" },
              { valor: "con", etiqueta: "Con tag" },
              { valor: "sin", etiqueta: "Sin tag" },
            ],
          },
        ]}
        total={productos.length}
        mostrados={productosFiltrados.length}
        onLimpiar={limpiarFiltros}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Descripción</StyledTableCell>
              <StyledTableCell>Serie</StyledTableCell>
              <StyledTableCell>Tags</StyledTableCell>
              <StyledTableCell>Estado</StyledTableCell>
              <StyledTableCell align="center">Acciones</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {productosFiltrados.map(p => (
              <TableRow
                key={p.id_producto}
                hover
                sx={{
                  cursor: "pointer",
                  opacity: p.isDisable ? 0.45 : 1,
                  backgroundColor: p.isDisable ? "#f5f5f5" : "inherit"
                }}
                onClick={() => {
                  setProductoDetalle(p);
                  setOpenDetalleModal(true);
                }}
              >
                <TableCell>{p.descripcion}</TableCell>
                <TableCell>{p.numero_serie}</TableCell>

                <TableCell>
                  {p.tag ? (
                    <Chip
                      label={p.tag.tag}
                      color="secondary"
                      size="small"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Sin tag
                    </Typography>
                  )}
                </TableCell>

                <TableCell>{p.isDisable ? "Deshabilitado" : "Activo"}</TableCell>

                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                  {/* Editar */}
                  <IconButton onClick={() => {
                    setEditMode(true);
                    setNewProducto({ ...defaultProducto, ...p });
                    setOpenProductoModal(true);
                  }} title="Editar">
                    <FontAwesomeIcon icon={faEdit} color="#f57c00" />
                  </IconButton>

                  {/* Asignar Tag (bloquea si ya tiene) */}
                  <IconButton
                    onClick={() => {
                      if (p.tag) {
                        Swal.fire(
                          "Ya tiene tag",
                          `Este producto ya tiene un tag activo (${p.tag?.tag || p.tag?.codigo || "asignado"}).`,
                          "info"
                        );
                        return;
                      }
                      setSelectedProductoId(p.id_producto);
                      setOpenTagsModal(true);
                    }}
                    title={p.tag ? "Ya tiene tag asignado" : "Asignar tag"}
                  >
                    <FontAwesomeIcon icon={faTag} color={p.tag ? "#9e9e9e" : "#1976d2"} />
                  </IconButton>

                  {/* Habilitar / Deshabilitar */}
                  <IconButton onClick={() => handleToggleDisable(p)} title={p.isDisable ? "Habilitar" : "Deshabilitar"}>
                    <FontAwesomeIcon
                      icon={p.isDisable ? faCheck : faBan}
                      color={p.isDisable ? "#2e7d32" : "#d32f2f"}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {productosFiltrados.length === 0 && (
              <SinResultados colSpan={5} total={productos.length} onLimpiar={limpiarFiltros} entidad="activos" />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {openProductoModal && (
        <ModalProducto
          open={openProductoModal}
          onClose={() => setOpenProductoModal(false)}
          editMode={editMode}
          newProducto={newProducto}
          setNewProducto={setNewProducto}
          errors={errors}
          tipo="producto"
          almacenes={almacenes}
          personales={personales}
          proveedores={proveedores}
          handleSaveProducto={handleSaveProducto}
          saving={saving}
        />
      )}

      {openDetalleModal && productoDetalle && (
        <ModalDetalleProducto
          open={openDetalleModal}
          onClose={() => setOpenDetalleModal(false)}
          producto={productoDetalle}
        />
      )}

      {openTagsModal && selectedProductoId && (
        <ModalAsignarTags
          open={openTagsModal}
          onClose={() => setOpenTagsModal(false)}
          idProducto={selectedProductoId}
          onTagSaved={fetchData}
        />
      )}

      {openTagsMultiplesModal && productosParaTags.length > 0 && (
        <ModalTagsMultiples
          open={openTagsMultiplesModal}
          onClose={() => setOpenTagsMultiplesModal(false)}
          productos={productosParaTags}
          onComplete={fetchData}
        />
      )}
    </>
  );
}

export default ProductosPage;
